import { LtInput, AdvancedMultiSelectDropdown, DateTimePicker } from 'limetray-ui-kit';
import { userManagerConstants } from '../../../../constants/userManagerConstants';
import LtLabel from '../../../../components/LtLabel';

const { labels, placeholders, genderOptions, countryCodes } =
  userManagerConstants.createUserModal;
const { checkIcon: CheckIcon, closeIcon: CloseIcon } = userManagerConstants.icons;

/**
 * UserBasicDetailsSection
 *
 * Renders the "Basic Information" form fields for user create/edit.
 * Used as a section component inside CreateEditUserModal → ManagerModal.
 *
 * Styles come exclusively from shared modal classes in UserManager.scss
 * and modal-specific classes in UserList/index.scss — no inline styles.
 *
 * @param {object}   props
 * @param {object}   props.formData  - Current field values
 * @param {function} props.onChange  - Called with (field, value) on change
 * @param {function} [props.onBlur]   - Called with (field) on input blur
 * @param {object}   props.errors    - Field-level error strings keyed by field name
 * @param {string}   [props.mode]    - 'create' | 'edit'
 * @param {function} [props.getFieldRuleStatuses] - Evaluates field rules in real-time
 */
function UserBasicDetailsSection({
  formData,
  onChange,
  onBlur = () => {},
  errors,
  mode = 'create',
  getFieldRuleStatuses = () => [],
}) {
  const isCreate = mode === 'create';

  const password = formData.password || '';
  const hasTyped = password.length > 0;
  const passwordRules = getFieldRuleStatuses('password');

  return (
    <div className="inputGrid">
      <div className="row col-2">
        {/* ── Full Name ── */}
        <div className="modal-form-field">
          <LtLabel required>{labels.name}</LtLabel>
          <LtInput
            placeholder={placeholders.name}
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            onBlur={() => onBlur('name')}
            className="modal-form-input"
            autoComplete="off"
            name="create-user-name"
            data-error={!!errors.name}
          />
          {errors.name && (
            <span className="modal-field-error">{errors.name}</span>
          )}
        </div>

        {/* ── Email ── */}
        <div className="modal-form-field">
          <LtLabel required>{labels.email}</LtLabel>
          <LtInput
            placeholder={placeholders.email}
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value)}
            onBlur={() => onBlur('email')}
            className="modal-form-input"
            autoComplete="off"
            name="create-user-email"
            data-error={!!errors.email}
          />
          {errors.email && (
            <span className="modal-field-error">{errors.email}</span>
          )}
        </div>
      </div>

      {/* ── Mobile Number: Country Code + Number ── */}
      <div className="row">
        <div className="modal-form-field">
          <LtLabel required>{labels.mobile}</LtLabel>
          <div className="modal-phone-row">
            {/* TODO: Replace static countryCodes with list fetched from the country codes API */}
            <div className={`modal-phone-code-dropdown ${!formData.countryCode ? 'is-placeholder' : ''}`} data-error={!!errors.countryCode}>
              <AdvancedMultiSelectDropdown
                options={countryCodes}
                defaultSelectedItems={formData.countryCode ? [formData.countryCode] : []}
                isMultiSelect={false}
                isCollapsible={false}
                isSearchable={true}
                borderRadius='8px'
                placeholder={placeholders.countryCode}
                onSelect={(selected) => {
                  const value = Array.isArray(selected) ? selected[0] : selected;
                  onChange('countryCode', value?.id || value || '');
                }}
                selectedBgColor="#e8f5e9"
                hoverBgColor="#f0f4f8"
                showSelectAll={false}
                removeBorderBetweenItems
              />
              {errors.countryCode && (
                <span className="modal-field-error">{errors.countryCode}</span>
              )}
            </div>
            <div className="modal-phone-number-input">
              <LtInput
                placeholder={placeholders.mobile}
                value={formData.mobile}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 10) {
                    onChange('mobile', val);
                  }
                }}
                onBlur={() => onBlur('mobile')}
                className="modal-form-input"
                autoComplete="off"
                name="create-user-mobile"
                data-error={!!errors.mobile}
              />
                {errors.mobile && (
                  <span className="modal-field-error">{errors.mobile}</span>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Password fields (create mode only) ── */}
      {isCreate && (
        <>
          <div className="row col-2">
            <div className="modal-form-field">
              <LtLabel required>{labels.password}</LtLabel>
              <LtInput
                placeholder={placeholders.password}
                value={formData.password}
                onChange={(e) => onChange('password', e.target.value)}
                onBlur={() => onBlur('password')}
                className="modal-form-input"
                type="password"
                autoComplete="new-password"
                name="create-user-password"
                data-error={!!errors.password}
              />
              {errors.password && (
                <span className="modal-field-error">{errors.password}</span>
              )}
            </div>

            <div className="modal-form-field">
              <LtLabel required>{labels.retypePassword}</LtLabel>
              <LtInput
                placeholder={placeholders.retypePassword}
                value={formData.retypePassword}
                onChange={(e) => onChange('retypePassword', e.target.value)}
                onBlur={() => onBlur('retypePassword')}
                className="modal-form-input"
                type="password"
                autoComplete="new-password"
                name="create-user-confirm-password"
                data-error={!!errors.retypePassword}
              />
              {errors.retypePassword && (
                <span className="modal-field-error">{errors.retypePassword}</span>
              )}
            </div>
          </div>

          {/* Password rule hints */}
          <div className="row">
            <ul className="modal-password-hints">
              {passwordRules.map((rule, i) => {
                const statusClass = !hasTyped ? 'neutral' : rule.isSatisfied ? 'satisfied' : 'unsatisfied';
                return (
                  <li key={i} className={`password-hint-item ${statusClass}`}>
                    <div className="hint-content">
                      {rule.isSatisfied ? (
                        <CheckIcon size="14" />
                      ) : (
                        <CloseIcon
                          size="14"
                          color={hasTyped ? '#EF4444' : '#9CA3AF'}
                        />
                      )}
                      <span>{rule.text}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

      <div className="row col-2">
        {/* ── Gender ── */}
        <div className="modal-form-field">
          <LtLabel>{labels.gender}</LtLabel>
          <div className={`modal-gender-dropdown ${!formData.gender ? 'is-placeholder' : ''}`}>
            <AdvancedMultiSelectDropdown
              options={genderOptions}
              defaultSelectedItems={formData.gender ? [formData.gender] : []}
              isMultiSelect={false}
              isCollapsible={false}
              isSearchable={false}
              placeholder={placeholders.gender}
              onSelect={(selected) => {
                const value = Array.isArray(selected) ? selected[0] : selected;
                onChange('gender', value?.id || value || '');
              }}
              selectedBgColor="#e8f5e9"
              hoverBgColor="#f0f4f8"
              borderRadius='8px'
              showSelectAll={false}
              removeBorderBetweenItems
            />
          </div>
        </div>

        {/* ── Date of Birth ── */}
        <div
          className="modal-form-field create-user-dob-field"
          onClick={(e) => {
            const target = e.target;
            if (
              target &&
              target.tagName === 'DIV' &&
              /^\d{1,2}$/.test(target.textContent?.trim()) &&
              target.parentNode &&
              target.parentNode.children.length >= 28
            ) {
              const style = window.getComputedStyle(target);
              if (style.cursor === 'not-allowed') {
                return;
              }
              setTimeout(() => {
                const buttons = Array.from(document.querySelectorAll('button'));
                const applyBtn = buttons.find(
                  (btn) => btn.textContent?.trim() === 'Apply'
                );
                if (applyBtn && !applyBtn.disabled) {
                  applyBtn.click();
                }
              }, 50);
            }
          }}
        >
          <LtLabel>{labels.dob}</LtLabel>
          <DateTimePicker
            value={formData.dob || undefined}
            onChange={(val) => onChange('dob', val.epoch || null)}
            includeTime={false}
            includeYearSelection
            placeholder='Select Date of Birth'
            maxDate={new Date()}
          />
        </div>
      </div>
    </div>
  );
}

export default UserBasicDetailsSection;

